import { useMemo, useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import Drawer from '../../components/Drawer';
import Spinner from '../../components/Spinner';
import {
  fosApi, fosError, isVersionConflict, KIND_LABEL, newRequestId, scopeFromKey, STATUS_LABEL,
  type DonorDetail, type DonorKind, type DonorStatus, type FosCallError, type FosContext,
} from '../../lib/fosApi';
import { ErrorNote } from './ui';

interface Props {
  context: FosContext;
  /** Present when editing an existing donor. */
  existing?: DonorDetail;
  onClose: () => void;
  onSaved: () => void;
}

/** Scopes in which the caller may create a donor, as relationship keys. */
function creatableScopes(context: FosContext): Array<{ key: string; label: string }> {
  const m = context.scopes.manageDonors;
  const all = m.council;
  return [
    ...(all ? [{ key: 'council', label: 'Council' }] : []),
    ...context.districts.filter((d) => all || m.districtIds.includes(d.id)).map((d) => ({ key: `district:${d.id}`, label: d.name })),
  ];
}
function contactAllowedIn(context: FosContext, key: string): boolean {
  const c = context.scopes.viewContact;
  return c.council || (key.startsWith('district:') && c.districtIds.includes(key.slice('district:'.length)));
}

export default function DonorFormDrawer({ context, existing, onClose, onSaved }: Props) {
  const navigate = useNavigate();
  const d = existing?.donor;
  const scopes = useMemo(() => creatableScopes(context), [context]);
  const [kind, setKind] = useState<DonorKind>(d?.kind ?? 'individual');
  const [name, setName] = useState(d?.displayName ?? '');
  const [status, setStatus] = useState<DonorStatus>(d?.status ?? 'active');
  const [email, setEmail] = useState(d?.contact?.emails.find((e) => e.primary)?.value ?? d?.contact?.emails[0]?.value ?? '');
  const [phone, setPhone] = useState(d?.contact?.phones[0] ?? '');
  const [addr, setAddr] = useState({ line1: d?.contact?.address?.line1 ?? '', line2: d?.contact?.address?.line2 ?? '', city: d?.contact?.address?.city ?? '', region: d?.contact?.address?.region ?? '', postalCode: d?.contact?.address?.postalCode ?? '' });
  const [primaryDistrict, setPrimaryDistrict] = useState(d?.primaryDistrict?.id ?? '');
  const [scopeKey, setScopeKey] = useState(scopes[0]?.key ?? 'council');
  const [summary, setSummary] = useState('');
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<FosCallError | null>(null);
  const [acknowledged, setAcknowledged] = useState<string[]>([]);

  const editing = !!existing;
  const showContact = editing ? existing!.permissions.editContact : contactAllowedIn(context, scopeKey);
  const lockedContact = editing && !existing!.permissions.editContact;
  const candidates = problem?.details?.candidates ?? [];

  const fieldsPayload = () => ({
    kind, displayName: name, status, primaryDistrictId: primaryDistrict || null,
    ...(showContact ? {
      emails: email.trim() ? [{ value: email.trim(), primary: true }] : [],
      phones: phone.trim() ? [phone.trim()] : [],
      address: Object.values(addr).some((v) => v.trim()) ? addr : null,
    } : {}),
  });

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true); setProblem(null);
    try {
      if (editing) {
        await fosApi.update({ requestId: newRequestId(), donorId: d!.id, expectedVersion: d!.version, fields: fieldsPayload() });
        onSaved();
      } else {
        const made = await fosApi.create({
          requestId: newRequestId(), donor: fieldsPayload(), scope: scopeFromKey(scopeKey), summary: summary.trim() || undefined,
          acknowledgedDuplicateDonorIds: acknowledged.length ? acknowledged : undefined,
        });
        onSaved();
        navigate(`/fos/donors/${made.donorId}`);
      }
    } catch (cause) { setProblem(fosError(cause)); } finally { setBusy(false); }
  };

  return (
    <Drawer title={editing ? 'Edit donor' : 'Add a donor'} subtitle={editing ? `${d!.displayName} · ${d!.ref}` : 'Record who they are and where the relationship lives.'} onClose={onClose}>
      <form onSubmit={submit} className="fos-form">
        <label>Type
          <select value={kind} onChange={(e) => setKind(e.target.value as DonorKind)}>
            {(Object.keys(KIND_LABEL) as DonorKind[]).map((k) => <option key={k} value={k}>{KIND_LABEL[k]}</option>)}
          </select>
        </label>
        <label>{kind === 'individual' ? 'Full name' : 'Name'}
          <input value={name} onChange={(e) => setName(e.target.value)} required maxLength={120} autoFocus />
        </label>
        {candidates.length > 0 && (
          <div className="fos-dup" role="alert">
            <strong>This may be a donor who is already recorded.</strong>
            <ul>
              {candidates.map((c) => (
                <li key={c.donorId}>
                  <button type="button" className="linkbtn" onClick={() => navigate(`/fos/donors/${c.donorId}`)}>{c.displayName}</button> <span className="subtitle">{c.ref} · matches {c.reasons.join(' and ')}</span>
                </li>
              ))}
            </ul>
            <label className="checkbox-row"><input type="checkbox" checked={candidates.every((c) => acknowledged.includes(c.donorId))}
              onChange={(e) => setAcknowledged(e.target.checked ? candidates.map((c) => c.donorId) : [])} />
              This is a different donor — add them anyway</label>
          </div>
        )}
        {editing && (
          <label>Status
            <select value={status} onChange={(e) => setStatus(e.target.value as DonorStatus)}>
              {(Object.keys(STATUS_LABEL) as DonorStatus[]).map((s) => <option key={s} value={s}>{STATUS_LABEL[s]}</option>)}
            </select>
          </label>
        )}
        {!editing && (
          <label>Relationship is held by
            <select value={scopeKey} onChange={(e) => setScopeKey(e.target.value)} required>
              {scopes.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
            </select>
            <small className="fos-hint">A donor can have a council relationship and district relationships, each with its own private notes and coordinator. You can add more later.</small>
          </label>
        )}
        {showContact ? (
          <fieldset className="fos-fieldset">
            <legend>Contact</legend>
            <label>Email<input type="email" value={email} onChange={(e) => setEmail(e.target.value)} maxLength={200} /></label>
            <label>Phone<input value={phone} onChange={(e) => setPhone(e.target.value)} maxLength={40} /></label>
            <label>Address<input value={addr.line1} onChange={(e) => setAddr({ ...addr, line1: e.target.value })} maxLength={120} placeholder="Street" /></label>
            <input value={addr.line2} onChange={(e) => setAddr({ ...addr, line2: e.target.value })} maxLength={120} placeholder="Apartment, suite…" aria-label="Address line 2" />
            <div className="form-row"><input value={addr.city} onChange={(e) => setAddr({ ...addr, city: e.target.value })} maxLength={80} placeholder="City" aria-label="City" />
              <input value={addr.region} onChange={(e) => setAddr({ ...addr, region: e.target.value })} maxLength={40} placeholder="State" aria-label="State" /></div>
            <input value={addr.postalCode} onChange={(e) => setAddr({ ...addr, postalCode: e.target.value })} maxLength={20} placeholder="ZIP" aria-label="ZIP" />
          </fieldset>
        ) : (
          <p className="fos-hint">{lockedContact ? 'You can edit this donor, but not their contact details.' : 'You do not have access to enter contact details in this scope.'}</p>
        )}
        <label>Main district <small className="fos-hint">(optional)</small>
          <select value={primaryDistrict} onChange={(e) => setPrimaryDistrict(e.target.value)}>
            <option value="">None</option>
            {context.districts.map((x) => <option key={x.id} value={x.id}>{x.name}</option>)}
          </select>
        </label>
        {!editing && <label>Relationship note <small className="fos-hint">(private to this relationship)</small>
          <textarea value={summary} onChange={(e) => setSummary(e.target.value)} maxLength={1000} placeholder="How do we know them? What matters to them?" /></label>}

        {problem && !candidates.length && <ErrorNote message={isVersionConflict(problem) ? 'This donor changed while you were editing. Close this panel and reopen it to see the latest.' : problem.message} />}
        {problem && candidates.length > 0 && !acknowledged.length && <p className="fos-hint">Open the existing donor, or confirm above that this is someone else.</p>}
        <div className="drawer-actions">
          <button type="button" className="btn" onClick={onClose}>Cancel</button>
          <button className="btn primary" disabled={busy || !name.trim() || (candidates.length > 0 && !candidates.every((c) => acknowledged.includes(c.donorId)))}>{busy ? <Spinner /> : editing ? 'Save changes' : 'Add donor'}</button>
        </div>
      </form>
    </Drawer>
  );
}
